import * as THREE from 'three';
import { SketchRelationKind } from '../../../../sketch/solver/constraint/relation-compatibility';
import { SingleTargetRelation } from '../../../../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import type { SketchBezierCurveMetadata } from '../../../../mesh/sketch/SketchSplineObject';
import type { SketchStyleSplineObject } from '../../../../mesh/sketch/SketchStyleSplineObject';
import { resolvePreferredEdgeLabel } from '../../model/EdgeLabelUtils';
import type { ReferenceDrivenStyleSplineOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenStyleSplineOnCurveConstraint';

export type { ReferenceDrivenStyleSplineOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenStyleSplineOnCurveConstraint';

/**
 * 参考驱动样式样条在曲线上关系定义。
 *
 * 关系实例只负责保存投影参数，禁止在自身内部运行点投影或几何刷新流程。
 */
export class ReferenceDrivenStyleSplineOnCurveConstraint extends SingleTargetRelation<SketchStyleSplineObject> {
	/** 引用记录稳定标识。 */
	public readonly referenceId: string;
	/** 关系面板使用的关系类型。 */
	public relationType = SketchRelationKind.OnEdge;

	/** 投影样式样条的世界坐标控制点。 */
	private readonly projectedControlPointWorlds: THREE.Vector3[];
	/** 用于克隆和持久化一致性的投影采样点。 */
	private readonly projectedCurvePointWorlds: THREE.Vector3[];
	/** 投影样式样条的参数曲线元数据。 */
	private readonly projectedCurveMetadata: SketchBezierCurveMetadata;
	/** 关系面板显示的来源边标签。 */
	private readonly edgeLabel: string;

	/** @param options 外部样式样条投影参数。 */
	public constructor(options: ReferenceDrivenStyleSplineOnCurveConstraintOptions) {
		super();
		this.referenceId = options.referenceId;
		this.projectedControlPointWorlds = options.projectedControlPointWorlds.map((point) => point.clone());
		this.projectedCurvePointWorlds = options.projectedCurvePointWorlds.map((point) => point.clone());
		this.projectedCurveMetadata = {
			...options.projectedCurveMetadata,
			weights: [...options.projectedCurveMetadata.weights],
		};
		this.edgeLabel = resolvePreferredEdgeLabel({
			runtimeSourceName: options.runtimeSourceName,
			persistedSourceDisplayLabel: options.sourceDisplayLabel,
		});
		this.name = this.edgeLabel;
	}

	/** @returns 关系面板使用的稳定显示名。 */
	public override get baseDisplayName(): string {
		return this.edgeLabel;
	}

	/** @returns 与当前投影载荷相互独立的新关系实例。 */
	public override cloneSelf(): ReferenceDrivenStyleSplineOnCurveConstraint {
		return new ReferenceDrivenStyleSplineOnCurveConstraint({
			referenceId: this.referenceId,
			projectedControlPointWorlds: this.projectedControlPointWorlds.map((point) => point.clone()),
			projectedCurvePointWorlds: this.projectedCurvePointWorlds.map((point) => point.clone()),
			projectedCurveMetadata: {
				...this.projectedCurveMetadata,
				weights: [...this.projectedCurveMetadata.weights],
			},
		});
	}

	/**
	 * 创建统一参数方程使用的局部样条载荷。
	 *
	 * @returns 投影控制点和参数曲线元数据；目标尚未绑定时返回 `null`。
	 */
	public override createParametricFormulaPayload() {
		const target = this.getTargetOrThrow();
		if (!target) {
			return null;
		}

		return {
			kind: 'external-spline',
			referenceId: this.referenceId,
			controlPoints: this.projectedControlPointWorlds.map((point) => {
				const local = target.referencePlane.worldToLocal(point);
				return [local.x, local.y];
			}),
			curveMetadata: {
				...this.projectedCurveMetadata,
				weights: [...this.projectedCurveMetadata.weights],
			},
		};
	}
}
